/**
 * Where to send a user who needs to leave the micro-apps host — back to the main SIYA
 * app, or to sign in.
 *
 * Both are derived from the CURRENT hostname rather than the tenant. Tenant comes from
 * the token and names the customer ('app', 'nyk'), not the environment, so a
 * tenant-derived link would send an app-dev user to production. Every deployed
 * micro-apps host is the main host with a `microapps` label bolted on
 * (app-dev-microapps, nyk-microapps, sandbox-microapps), so stripping that label keeps
 * the user in the environment they were already in.
 *
 * The bare `microapps.siya.com` has no label to strip and is shared by several tenants,
 * so that one case needs a node hint from the caller.
 */

import { decodeJwtPayload, isTenantLabel } from './handoverParams';
// Relative, not the '@/' alias, and that matters here. This module is shared: the browser
// imports it AND so does the server (/api/auth/login-path). The server is compiled by tsc,
// which does not rewrite path aliases, so an '@/' import resolves in the bundle and throws
// MODULE_NOT_FOUND at boot in the container. Anything reachable from server/ uses relative
// paths for that reason.
import { config } from '../config/runtime';

const MICROAPPS_LABEL = 'microapps';

/**
 * Where the sign-in form lives on the main host. There are two, and which one is right
 * depends on whether that host has been migrated to the SIYA SPA yet — the fleet is
 * mid-migration and both are live today:
 *
 *   /login       the SPA's route. synergy and nyk serve this.
 *   /login.html  the legacy static chat-interface page. sandbox and spar serve this,
 *                and answer 403 for /login.
 *
 * Sending the wrong one is not a cosmetic miss. On an SPA host /login.html matches no
 * route, so it falls to the router's catch-all — `<Navigate to="/login" replace />` —
 * which rebuilds the location from a bare path string and therefore DROPS THE QUERY.
 * returnUrl and logout both vanish before the login component ever mounts, so the user
 * is silently forwarded instead of being shown the form, and lands straight back where
 * they started. That is the sign-in button that "does nothing".
 *
 * Which host is which is discovered at runtime (see pages/api/auth/login-path.ts) rather
 * than listed here, so a tenant migrating to the SPA needs no change in this repo.
 *
 * There is deliberately NO default. Defaulting to either one is a guess, and a guess that
 * lands on a migrated host reproduces the original bug exactly: a button that navigates,
 * appears to work, and silently returns the user to where they started. Until the probe
 * answers, the path is null and no sign-in link is built — the same rule this file already
 * applies to an origin it cannot determine, for the same reason.
 */
export const SPA_LOGIN_PATH = '/login';
export const LEGACY_LOGIN_PATH = '/login.html';

/**
 * The main SIYA origin for this host, or null when it cannot be determined (localhost,
 * an unrecognised host, or bare microapps with no node hint). Callers treat null as
 * "don't navigate".
 */
export function mainDomainFor(hostname: string): string | null {
  const dot = hostname.indexOf('.');
  if (dot < 1) return null; // localhost and other single-label hosts

  const label = hostname.slice(0, dot);
  const base = hostname.slice(dot + 1);

  if (label.endsWith(`-${MICROAPPS_LABEL}`)) {
    return `https://${label.slice(0, -(MICROAPPS_LABEL.length + 1))}.${base}`;
  }

  // Deliberately NOT `https://<tenant>.<base>` for the bare microapps host. Only some
  // tenants have a matching host — app, app-dev, nyk, synergy, sandbox and spar resolve,
  // while kline, asahi, onesea, navios and cognixone do not — so deriving one from the
  // tenant produces a dead link for half the fleet. The caller supplies a configured
  // origin for this case instead.
  return null;
}

/**
 * The MAIN SIYA origin to send this visitor to for sign-in — origin only, no path.
 *
 * The login page is served by the main app's distribution, NOT by the micro-apps host —
 * the two are separate origins, so a same-origin link 404s. That broke sign-in for anyone
 * arriving from an emailed micro-apps link.
 *
 * Resolution order. The only tenant named in source is the shared production host's last-
 * resort fallback, which the login-path probe still has to reach before it is used:
 *   1. the hostname's own label, which is exact and needs no configuration
 *   2. `https://<node>.<base>`, for the shared microapps host where the hostname names no
 *      single tenant but the caller knows which one we are serving
 *   3. configuredOrigin, i.e. NEXT_PUBLIC_LOGIN_ORIGIN, and when that is unset on the shared
 *      production host, synergy — see defaultSignInOriginFor
 *
 * The session tenant is deliberately NOT used to build a host in step 1 — see
 * mainDomainFor. Only some tenants have a <tenant>.siya.com; kline, asahi, onesea, navios
 * and cognixone do not, so a tenant-derived login URL is dead for them.
 *
 * Sending every tenant to one configured login host is safe because that host does not
 * decide which org the user signs into: login POSTs only {email, password} to IAM and
 * sends no x-org-domain — that header is used by profile, change-password and
 * forgot-password, not login — so IAM resolves the org from the credentials.
 */
export function signInOriginFor(
  hostname: string,
  configuredOrigin?: string | null,
  node?: string | null,
): string | null {
  return signInOriginCandidates(hostname, configuredOrigin, node)[0] ?? null;
}

/**
 * Every main-host candidate for this visitor, best first — and why one is not enough.
 *
 * tenantDomainFor CONSTRUCTS `https://<tenant>.<base>` from a name. It is first (after the
 * hostname's own label) for a real reason — the login page picks the SSO identity provider
 * from its own hostname, so a tenant with a host must be sent to it — but the construction
 * is not evidence that the host exists. On the shared `microapps.siya.com`, where the
 * hostname names no tenant, tenants whose subdomain was never created resolve to a host
 * that does not exist: the sign-in origin comes back non-null, the login-path probe fails
 * against it, and because a non-null answer ends the old `??` chain, the referrer and the
 * configured origin — the two candidates that would have worked — were never tried. The
 * user gets an Access Denied card with no way in, permanently, for that tenant.
 *
 * So the order is kept as a PREFERENCE and the caller walks it, taking the first candidate
 * the login-path probe can actually reach (see hooks/useSignInUrl). Nothing is guessed:
 * a candidate is used only once the host has answered for itself.
 *
 * Deduped, because the same origin can arrive from two of these and probing it twice only
 * costs the user time.
 */
export function signInOriginCandidates(
  hostname: string,
  configuredOrigin?: string | null,
  node?: string | null,
): string[] {
  const sameEnvironment = (o: string) => servesSameEnvironmentAs(hostname, o);

  // Everything here is DERIVED — from the hostname, the token's tenant, the referrer — so
  // each one is a guess that has to be checked against the environment we are serving.
  const derived = [
    mainDomainFor(hostname),
    tenantDomainFor(hostname, node),
    referrerOriginFor(hostname),
  ].filter((o): o is string => Boolean(o)).filter(sameEnvironment);

  // NEXT_PUBLIC_LOGIN_ORIGIN is exempt, and that is the point of it: an operator naming a
  // host outright is stating intent, not guessing, and it is the documented way to express
  // an arrangement this file's heuristics cannot derive — a hyphenated tenant name, say.
  // Filtering it would silently discard the one setting that exists to overrule the
  // filter, leaving no way to describe such a deployment at all.
  const configured = normaliseOrigin(configuredOrigin) ?? defaultSignInOriginFor(hostname);

  const ordered = [...derived, ...(configured ? [configured] : [])];

  return Array.from(new Set(ordered));
}

/**
 * Production's shared micro-apps host, and the main host its visitors sign in on when
 * nothing else names one.
 */
const SHARED_PRODUCTION_HOST = 'microapps.siya.com';
const SHARED_PRODUCTION_SIGN_IN_ORIGIN = 'https://synergy.siya.com';

/**
 * The sign-in origin to use when NEXT_PUBLIC_LOGIN_ORIGIN is not set — last in line, after
 * every derived candidate.
 *
 * Only the shared production host gets one. Every other deployed host names its own
 * environment (app-dev-microapps, spar-microapps, nyk-microapps) and derives its login host
 * from that, so a default there could only ever send a user across environments.
 *
 * Without it, a visitor on microapps.siya.com with no stored tenant, no referrer and no
 * configured origin — a typed or bookmarked URL after signing out — gets the Secure Access
 * card with no button and no redirect. The value is still probed by /api/auth/login-path
 * before it is used, so a host that stops answering produces no link rather than a dead one.
 */
function defaultSignInOriginFor(hostname: string): string | null {
  return hostname.toLowerCase() === SHARED_PRODUCTION_HOST ? SHARED_PRODUCTION_SIGN_IN_ORIGIN : null;
}

/**
 * The environment-qualifying part of a host label, or null if the label names a plain
 * tenant.
 *
 * Tenant labels are single tokens — synergy, spar, nyk, sandbox, navios, cognixone. An
 * environment-qualified label carries a suffix after a hyphen: `app-dev` is the `dev`
 * deployment of `app`. That shape is the only thing separating the two at runtime, and it
 * has to be settled at runtime because nothing in the browser knows which deployments
 * exist.
 *
 * A hyphenated TENANT would be misread as an environment by this. The escape hatch is
 * NEXT_PUBLIC_LOGIN_ORIGIN, which names the host outright and is exempt from the filter
 * for exactly that reason. No such tenant exists today.
 */
function environmentSuffix(label: string): string | null {
  const hyphen = label.indexOf('-');
  return hyphen > 0 ? label.slice(hyphen + 1) : null;
}

/** The label of a micro-apps host, with its `-microapps` suffix removed. */
function environmentLabelOf(hostname: string): string | null {
  const dot = hostname.indexOf('.');
  if (dot < 1) return null;

  const label = hostname.slice(0, dot).toLowerCase();
  if (label === MICROAPPS_LABEL) return null;                    // the shared production host
  if (label.endsWith(`-${MICROAPPS_LABEL}`)) return label.slice(0, -(MICROAPPS_LABEL.length + 1));
  return null;
}

/**
 * Would sending this visitor to `origin` move them into a DIFFERENT environment?
 *
 * This is the check that stopped the fleet's two environments swapping sessions. Sign-in
 * hosts were resolved with no notion of environment at all, so `app-dev-microapps` sent
 * users to production's login and `microapps.siya.com` sent them to app-dev's. Each user
 * came back holding a token minted for the environment they had just visited — and the
 * pod they returned to has no database configured for that tenant, so it answered 500 (or
 * now 409) and refused them. Both directions were live simultaneously, and the pod logs
 * showed it: production holding only `app-dev` sessions, app-dev holding only `synergy`.
 *
 * The rule is the host's own name, which is the one thing that reliably states which
 * deployment is serving this request:
 *
 *   `app-dev-microapps.siya.com`  is the app-dev environment — only `app-dev.siya.com`
 *   `nyk-microapps.siya.com`      is nyk's own host        — only `nyk.siya.com`
 *   `microapps.siya.com`          is production           — any tenant, no environment-
 *                                                           qualified label
 *
 * The production case still admits every tenant, which is deliberate: that host is shared,
 * and the login page picks its SSO identity provider from its own hostname, so a spar user
 * must still reach spar.siya.com. What it refuses is a label that names an ENVIRONMENT
 * rather than a tenant — an `app-dev` session on the production host is the crossing
 * itself, and following it would hand the user back the same unusable token.
 */
function servesSameEnvironmentAs(hostname: string, origin: string): boolean {
  let candidateLabel: string;
  try {
    const host = new URL(origin).hostname.toLowerCase();
    const dot = host.indexOf('.');
    if (dot < 1) return false;
    candidateLabel = host.slice(0, dot);
  } catch {
    return false;
  }

  const environment = environmentLabelOf(hostname);

  // A host that names its environment accepts exactly that environment's main host.
  if (environment) return candidateLabel === environment;

  // The shared production host: every tenant, but nothing that names another environment.
  return environmentSuffix(candidateLabel) === null;
}

/**
 * The main SIYA host the visitor arrived FROM.
 *
 * On the shared microapps host the hostname names no tenant, and a visitor who has never
 * completed a sign-in here has nothing stored to name one either — which is how the card
 * ends up with no button at all. But most of those visitors did not type the URL: they
 * followed a link out of the main app, or they are inside its iframe. Either way the
 * browser tells us which host that was.
 *
 * That is evidence, not a guess. It is the one thing on this page that reports where the
 * user actually came from, and it is exactly the host their session lives on.
 *
 * It is worth nothing for a bookmark or a typed URL — `document.referrer` is empty then —
 * and it is deliberately NOT filled in with something plausible; see the constants at the
 * top of this file. The referrer is also only ever the ORIGIN under the default
 * strict-origin-when-cross-origin policy, which is all this needs.
 *
 * Everything about it is checked before use, because a referrer is set by whoever linked
 * here: https only, a single label, under the SAME base domain as this host, not this host
 * itself, and not another micro-apps host — that last one would send the user in a circle.
 */
function referrerOriginFor(hostname: string): string | null {
  if (typeof document === 'undefined') return null;

  const referrer = document.referrer;
  if (!referrer) return null;

  const dot = hostname.indexOf('.');
  if (dot < 1) return null;
  const base = hostname.slice(dot + 1).toLowerCase();

  let url: URL;
  try {
    url = new URL(referrer);
  } catch {
    return null;
  }

  if (url.protocol !== 'https:') return null;

  const host = url.hostname.toLowerCase();
  if (host === hostname.toLowerCase()) return null;
  if (!host.endsWith(`.${base}`)) return null;

  const label = host.slice(0, host.length - base.length - 1);
  if (!isTenantLabel(label)) return null;
  if (label === MICROAPPS_LABEL || label.endsWith(`-${MICROAPPS_LABEL}`)) return null;

  return `https://${host}`;
}

/**
 * The full sign-in URL for this host, carrying returnUrl so the user lands back on the
 * page they were trying to reach.
 *
 * The round trip holds for every micro-apps host: the login page accepts a returnUrl
 * whose hostname ends in siya.com or siya-ai.com and redirects back to it with authToken
 * appended, which is what AuthGuard consumes on arrival.
 *
 * `loginPath` decides WHICH login page — see the constants at the top of this file. It is
 * required and may be null: null means the probe has not answered, and the answer is no
 * URL at all rather than a guessed one.
 */
export function loginUrlFor(
  hostname: string,
  returnUrl: string,
  configuredOrigin: string | null | undefined,
  node: string | null | undefined,
  forceReauth: boolean,
  loginPath: string | null,
): string | null {
  return signInUrlFor(
    signInOriginFor(hostname, configuredOrigin, node),
    returnUrl,
    forceReauth,
    loginPath,
  );
}

/**
 * The same URL, built from an origin the caller has ALREADY established is reachable —
 * which is what useSignInUrl does after walking signInOriginCandidates. Both arguments may
 * be null and null in means null out: no origin, or no probed path, means no link.
 */
export function signInUrlFor(
  main: string | null,
  returnUrl: string,
  forceReauth: boolean,
  loginPath: string | null,
): string | null {
  if (!main || !loginPath) return null;

  // Both login pages forward an already-"authenticated" visitor onward without ever
  // rendering the form — the check is the raw `isLoggedIn` flag, NOT whether the stored
  // token is still valid:
  //
  //     if (localStorage.getItem('isLoggedIn')) handlePostLoginNavigation()
  //
  // So a user whose stored token has EXPIRED still counts as logged in there. They are
  // forwarded straight back to us carrying the same dead token, we reject it, and they
  // are back on the card they just clicked out of. Clicking again repeats it forever —
  // the loop that looks like the button doing nothing. ?logout clears that stale flag
  // first, so the credentials form actually appears.
  //
  // returnUrl is sent too so the user comes back to the micro-app after signing in. NOTE:
  // the login page currently drops it on this path — its logout branch replaces the URL
  // with a bare pathname and returns BEFORE the line that saves returnUrl — so until that
  // is fixed there, the user lands wherever their post-login default sends them. For the
  // onlyMicroapps users this matters most to, that default is this host, so they still
  // arrive. Sending it costs nothing and makes the shell correct already.
  if (forceReauth) {
    return `${main}${loginPath}?logout=true&returnUrl=${encodeURIComponent(returnUrl)}`;
  }

  return `${main}${loginPath}?returnUrl=${encodeURIComponent(returnUrl)}`;
}

/**
 * `https://<tenant>.<base>` for the shared micro-apps host, derived from whatever tenant
 * we know — no list of tenant names anywhere, so a tenant added tomorrow works with no
 * code or config change.
 *
 * This matters beyond cosmetics: the login page picks which identity provider SSO uses
 * from its OWN hostname — extractTenant() in the chat interface reads hostname.split('.')[0]
 * and passes it to /auth/microsoft/sso-login/url?tenant=. Sending a spar user to a generic
 * login host would SSO them against the wrong tenant. (Password login is unaffected: it
 * POSTs only {email, password} and IAM resolves the org from the credentials.)
 *
 * The base domain comes from the host we are on, so siya-ai.com and any future domain
 * follow automatically.
 *
 * `node` is validated before it is interpolated — see isTenantLabel. It reaches here from
 * a query param and from localStorage, so without that check a crafted link points the
 * sign-in button at an arbitrary host.
 */
function tenantDomainFor(hostname: string, node?: string | null): string | null {
  if (!isTenantLabel(node)) return null;

  const dot = hostname.indexOf('.');
  if (dot < 1) return null;

  const base = hostname.slice(dot + 1);
  return `https://${node}.${base}`;
}

/**
 * An origin that is safe to send a user to: https, a SIYA host, nothing else attached.
 *
 * Used on every origin that reaches the browser from outside this module — the one the
 * login-path probe resolved to, and the one read back out of localStorage — because both
 * end up as the href of a button we are telling the user is safe to click. The API applies
 * the same rule server-side; this is the second half of it, at the point of use.
 */
export function normaliseSiyaOrigin(value: string): string | null {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }

  if (url.protocol !== 'https:') return null;
  if (url.username || url.password || url.port) return null;

  const host = url.hostname.toLowerCase();
  const allowed = SIYA_BASE_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`));
  return allowed ? `https://${host}` : null;
}

/** The domains a SIYA main host can live under. Mirrored by the login-path API. */
const SIYA_BASE_DOMAINS = ['siya.com', 'siya-ai.com'];

/** Tolerates a configured value with or without a scheme, and a trailing slash. */
function normaliseOrigin(origin?: string | null): string | null {
  const trimmed = origin?.trim().replace(/\/+$/, '');
  if (!trimmed) return null;
  // Without this, "app.siya.com" yields a RELATIVE href, so the browser navigates to
  // <current-host>/app.siya.com/login.html and the button appears to do nothing useful.
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

const readCookie = (name: string): string | null =>
  document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${name}=`))
    ?.split('=')[1] ?? null;

/**
 * The tenant name a stored credential carries, even an expired one.
 *
 * Expiry is deliberately not checked. A spent token is useless AS a credential and is
 * refused everywhere else, but the tenant inside it is still a true statement about who
 * this browser belongs to — and that is all this is used for: choosing which main host
 * to send the user to for sign-in.
 */
function nodeFromStoredToken(): string | null {
  const token = localStorage.getItem('authToken') ?? readCookie('authToken');
  if (!token) return null;
  return decodeJwtPayload(token)?.data?.tenant ?? null;
}

/**
 * The tenant to build the sign-in host from.
 *
 * Every source is evidence this browser actually holds, checked in descending order of
 * how current it is. None of it is a guess: when nothing here knows the tenant, the
 * answer is null and no sign-in link is built.
 *
 * The list is long because the bare microapps host is shared between tenants and the
 * hostname names none of them, so the difference between finding a tenant and not is the
 * difference between a working button and a card with nothing on it. The screenshot of
 * that empty card is what the last three entries exist for: a returning visitor whose
 * `node` key was never written still has a tenant recorded somewhere.
 */
function resolveNode(tenant?: string | null): string | null {
  if (isTenantLabel(tenant)) return tenant;

  try {
    const candidates = [
      // The chat appends this when it opens micro-apps.
      new URLSearchParams(window.location.search).get('node'),
      // Written by AuthGuard from a previous ?node=.
      localStorage.getItem('node'),
      // Written by storeAuthToken from the token's own claims.
      localStorage.getItem('tenant'),
      // Written by middleware alongside the handover cookie.
      readCookie('tenant'),
      // Last: read it out of whatever credential is still lying around, expired or not.
      nodeFromStoredToken(),
    ];

    return candidates.find(isTenantLabel) ?? null;
  } catch {
    return null;
  }
}

/**
 * Browser-side: the main SIYA origin this page would send a visitor to for sign-in.
 * Null when it cannot be determined. Exposed so the login-path probe knows which host
 * to ask about.
 */
export function resolveSignInOrigin(tenant?: string | null): string | null {
  return resolveSignInOriginCandidates(tenant)[0] ?? null;
}

/**
 * Browser-side: every main SIYA origin worth trying for this visitor, best first. Empty on
 * the server and when none can be determined. See signInOriginCandidates for why the
 * caller must walk the list rather than take the head.
 */
export function resolveSignInOriginCandidates(tenant?: string | null): string[] {
  if (typeof window === 'undefined') return [];

  return signInOriginCandidates(
    window.location.hostname,
    config.loginOrigin,
    resolveNode(tenant),
  );
}

/**
 * Browser-side wrapper around loginUrlFor: the sign-in URL for the page the user is
 * currently on. Null when it cannot be determined, and the caller renders no button
 * rather than a dead one.
 *
 * `origin` and `loginPath` both come from useSignInUrl, which walks the origin candidates
 * and probes the main host. Either being null means the caller renders no button, rather
 * than one that is wrong on half the fleet or points at a host that does not exist.
 */
export function resolveSignInUrl(
  origin: string | null,
  forceReauth: boolean,
  loginPath: string | null,
): string | null {
  if (typeof window === 'undefined') return null;

  return signInUrlFor(origin, window.location.href, forceReauth, loginPath);
}
